import { useEffect, type ReactNode } from "react";
import { AppBar, Layout as RaLayout, Menu, Sidebar, TitlePortal, usePermissions, useSidebarState, type SidebarProps } from "react-admin";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { Theme } from "@mui/material/styles";
import Divider from "@mui/material/Divider";
import ListSubheader from "@mui/material/ListSubheader";
import DashboardIcon from "@mui/icons-material/SpaceDashboardOutlined";
import PeopleIcon from "@mui/icons-material/PeopleAltOutlined";
import PercentIcon from "@mui/icons-material/PercentOutlined";
import MapIcon from "@mui/icons-material/GridOnOutlined";
import GavelIcon from "@mui/icons-material/GavelOutlined";
import RuleIcon from "@mui/icons-material/RuleOutlined";
import HistoryIcon from "@mui/icons-material/HistoryOutlined";
import LanguageIcon from "@mui/icons-material/LanguageOutlined";
import KeyIcon from "@mui/icons-material/BadgeOutlined";
import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import InventoryIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingIcon from "@mui/icons-material/LocalShippingOutlined";
import TuneIcon from "@mui/icons-material/TuneOutlined";
import AccountBalanceIcon from "@mui/icons-material/AccountBalanceOutlined";
import FactCheckIcon from "@mui/icons-material/FactCheckOutlined";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import LocalShippingTruckIcon from "@mui/icons-material/AirportShuttleOutlined";
import AssignmentIcon from "@mui/icons-material/AssignmentOutlined";
import TravelExploreIcon from "@mui/icons-material/TravelExploreOutlined";
import CalendarIcon from "@mui/icons-material/CalendarMonthOutlined";
import type { Permissoes } from "../lib/provedores";

function Barra() {
  return (
    <AppBar color="primary">
      <Box
        component="img"
        src="/logo-icone-192.png"
        alt="Sobral Nunes"
        sx={{ height: 28, width: 28, mr: 1.5, bgcolor: "#fff", borderRadius: 1, p: 0.25 }}
      />
      <TitlePortal />
    </AppBar>
  );
}

/** Título de seção do menu; com o menu recolhido vira só um divisor. */
function Secao({ titulo }: { titulo: string }) {
  const [aberto] = useSidebarState();
  return aberto ? (
    <ListSubheader
      disableSticky
      sx={{ bgcolor: "transparent", lineHeight: "32px", mt: 1, fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase" }}
    >
      {titulo}
    </ListSubheader>
  ) : (
    <Divider sx={{ my: 1 }} />
  );
}

function MenuLateral() {
  const { permissions } = usePermissions<Permissoes | null>();
  const admin = permissions?.papel === "admin";
  return (
    <Menu>
      <Menu.DashboardItem primaryText="Painel" leftIcon={<DashboardIcon />} />
      <Secao titulo="Licitações" />
      <Menu.Item to="/pncp_contratacao" primaryText="Buscar licitações" leftIcon={<TravelExploreIcon />} />
      <Menu.Item to="/licitacao" primaryText="Licitações" leftIcon={<AssignmentIcon />} />
      <Menu.Item to="/calendario" primaryText="Calendário" leftIcon={<CalendarIcon />} />
      <Secao titulo="Cadastros" />
      <Menu.Item to="/produto" primaryText="Produtos" leftIcon={<InventoryIcon />} />
      <Menu.Item to="/fornecedor" primaryText="Fornecedores" leftIcon={<LocalShippingIcon />} />
      <Menu.Item to="/pessoa" primaryText="Órgãos e clientes" leftIcon={<AccountBalanceIcon />} />
      <Menu.Item to="/transportadora" primaryText="Transportadoras" leftIcon={<LocalShippingTruckIcon />} />
      <Menu.Item to="/portal" primaryText="Portais" leftIcon={<LanguageIcon />} />
      <Menu.Item to="/conta_portal" primaryText="Contas em portais" leftIcon={<KeyIcon />} />
      <Secao titulo="Habilitação" />
      <Menu.Item to="/documento_habilitacao" primaryText="Documentos" leftIcon={<FactCheckIcon />} />
      <Menu.Item to="/atestado_capacidade" primaryText="Atestados" leftIcon={<WorkspacePremiumIcon />} />
      <Secao titulo="Parâmetros" />
      <Menu.Item to="/parametro_preco_padrao" primaryText="Padrões de preço" leftIcon={<PercentIcon />} />
      <Menu.Item to="/v_aliquota_icms_vigente" primaryText="Matriz de ICMS" leftIcon={<MapIcon />} />
      <Menu.Item to="/parametro_fiscal" primaryText="Parâmetros fiscais" leftIcon={<GavelIcon />} />
      <Menu.Item to="/regra_preco" primaryText="Regra de preço" leftIcon={<RuleIcon />} />
      <Menu.Item to="/parametro_sistema" primaryText="Parâmetros do sistema" leftIcon={<TuneIcon />} />
      {admin && <Secao titulo="Administração" />}
      {admin && <Menu.Item to="/empresa" primaryText="Dados da empresa" leftIcon={<BusinessIcon />} />}
      {admin && <Menu.Item to="/usuario" primaryText="Usuários" leftIcon={<PeopleIcon />} />}
      {admin && <Menu.Item to="/auditoria" primaryText="Auditoria" leftIcon={<HistoryIcon />} />}
    </Menu>
  );
}

/**
 * Abaixo de 900 px o menu vira gaveta sobre o conteúdo (abre pelo botão da barra e fecha ao escolher a tela).
 * O react-admin só faz isso abaixo de 600 px; entre 600 e 900 px o menu fixo de 240 px cobria a borda
 * esquerda das telas na janela estreita.
 */
function MenuLateralResponsivo(props: SidebarProps) {
  const estreito = useMediaQuery<Theme>((theme) => theme.breakpoints.down("md"));
  const [aberto, setAberto] = useSidebarState();
  // Ao estreitar a janela, a gaveta começa fechada (o estado "aberto" do desktop fica guardado no navegador).
  useEffect(() => {
    if (estreito) setAberto(false);
  }, [estreito]);
  if (!estreito) return <Sidebar {...props} />;
  return (
    <Drawer variant="temporary" open={aberto} onClose={() => setAberto(false)} slotProps={{ paper: { sx: { width: 260, pt: 1 } } }}>
      {props.children}
    </Drawer>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  return (
    <RaLayout appBar={Barra} menu={MenuLateral} sidebar={MenuLateralResponsivo}>
      {children}
    </RaLayout>
  );
}
